import { useState, useEffect } from 'react';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { Globe, Save, CheckCircle2, RefreshCw, ArrowRightLeft } from 'lucide-react';
import { resolveEffectiveExchangeRate } from '@/shared/utils/currencyConversion';
import { formatINRPaise } from '@/shared/utils/currency';

interface CurrencyRateRow {
  code: string;
  name: string;
  symbol: string;
  flag: string;
}

const SUPPORTED_CURRENCIES: CurrencyRateRow[] = [
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', flag: '🇸🇦' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', flag: '🇨🇦' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵' },
];

export function LocaleSettingsPage() {
  const [baseCurrency, setBaseCurrency] = useState('INR');
  const [permittedCurrencies, setPermittedCurrencies] = useState(['INR', 'USD', 'EUR', 'GBP']);
  const [exchangeRateSource, setExchangeRateSource] = useState('manual');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [locale, setLocale] = useState('en-IN');
  const [currencyPrecision, setCurrencyPrecision] = useState(2);
  const [quantityPrecision, setQuantityPrecision] = useState(2);
  
  // Manual overrides map: { USD: 89.00, EUR: 105.00, ... }
  const [manualRates, setManualRates] = useState<Record<string, string>>({});
  // Live rates fetched from forex API (1 Target = X INR)
  const [liveRates, setLiveRates] = useState<Record<string, number>>({});
  const [fetchingLive, setFetchingLive] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const loadLiveRates = async () => {
    setFetchingLive(true);
    try {
      const res = await fetch('/api/forex/rates?base=INR&quotes=USD,EUR,GBP,AED,SAR,CAD,SGD,JPY');
      if (res.ok) {
        const json = await res.json();
        const map: Record<string, number> = {};
        (json.rates || []).forEach((r: any) => {
          // inrRate or 1/rate gives 1 Target = X INR
          const val = r.liveInrRate || (r.marketRate > 0 ? Number((1 / r.marketRate).toFixed(4)) : (r.rate > 0 ? Number((1 / r.rate).toFixed(4)) : 0));
          if (val > 0) map[r.quote.toUpperCase()] = val;
        });
        setLiveRates(map);
      }
    } catch (e) {
      console.error('Failed to load live rates:', e);
    } finally {
      setFetchingLive(false);
    }
  };

  useEffect(() => {
    Promise.all([
      fetch('/api/accounting/org/locale').then((res) => res.json()),
      loadLiveRates(),
    ])
      .then(([localeData]) => {
        if (localeData?.ok || localeData?.success) {
          const d = localeData.data;
          if (d) {
            if (d.baseCurrency) setBaseCurrency(d.baseCurrency);
            if (Array.isArray(d.permittedCurrencies)) setPermittedCurrencies(d.permittedCurrencies);
            if (d.exchangeRateSource) setExchangeRateSource(d.exchangeRateSource);
            if (d.timezone) setTimezone(d.timezone);
            if (d.locale) setLocale(d.locale);
            if (d.currencyPrecision !== undefined) setCurrencyPrecision(d.currencyPrecision);
            if (d.quantityPrecision !== undefined) setQuantityPrecision(d.quantityPrecision);
            if (d.manualExchangeRates) {
              const formatted: Record<string, string> = {};
              Object.entries(d.manualExchangeRates).forEach(([k, v]) => {
                if (v !== undefined && v !== null && Number(v) > 0) {
                  formatted[k.toUpperCase()] = String(v);
                }
              });
              setManualRates(formatted);
            }
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleManualRateChange = (code: string, value: string) => {
    setManualRates((prev) => {
      const next = { ...prev };
      if (!value || value.trim() === '') {
        delete next[code];
      } else {
        next[code] = value;
      }
      return next;
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const numericManualRates: Record<string, number> = {};
      Object.entries(manualRates).forEach(([k, v]) => {
        const num = Number(v);
        if (!isNaN(num) && num > 0) {
          numericManualRates[k] = num;
        }
      });

      const res = await fetch('/api/accounting/org/locale', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseCurrency,
          permittedCurrencies,
          exchangeRateSource,
          timezone,
          locale,
          currencyPrecision,
          quantityPrecision,
          manualExchangeRates: numericManualRates,
        }),
      });
      const data = await res.json();
      if (res.ok && (data.ok || data.success)) {
        setToast('Locale, currency, and precision settings saved successfully!');
        setTimeout(() => setToast(null), 3000);
      } else {
        alert(data.error?.message || 'Failed to save locale settings.');
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <SettingsSubNav />

      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {toast && (
          <div className="p-4 bg-success/10 dark:bg-success border border-success/25 dark:border-success/40 rounded-xl text-success-text dark:text-success-text text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success-text" />
            {toast}
          </div>
        )}

        <form onSubmit={handleSave} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-6">
          <div className="border-b border-slate-200 dark:border-slate-800 pb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-500" /> Currency & Exchange Rate Architecture
              </h2>
              <p className="text-xs text-slate-500">
                INR is the canonical base pricing currency (1 Foreign Unit = X INR). Foreign prices are derived dynamically: <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">Foreign Price = INR Base Price / Exchange Rate</span>.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading locale and currency settings...</div>
          ) : (
            <div className="space-y-6">
              {/* Currency Architecture Info Banner */}
              <div className="p-4 bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs space-y-1">
                <div className="flex items-center gap-2 font-bold text-indigo-900 dark:text-indigo-200">
                  <ArrowRightLeft className="w-4 h-4 text-indigo-600" />
                  Canonical INR Base Price Direction Rule
                </div>
                <p className="text-indigo-800/80 dark:text-indigo-300">
                  When USD/INR rate increases (e.g. ₹89 → ₹90), converted USD product prices <strong className="font-bold underline">decrease</strong> ($10.00 → $9.89). Effective Rate follows <span className="font-mono font-semibold">manualExchangeRate ?? liveExchangeRate</span>.
                </p>
              </div>

              {/* Currency Settings Table */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Exchange Rates & Admin Overrides</h3>
                    <p className="text-xs text-slate-500">Set manual exchange rate overrides per currency or leave empty to use the live interbank feed.</p>
                  </div>
                  <button
                    type="button"
                    onClick={loadLiveRates}
                    disabled={fetchingLive}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${fetchingLive ? 'animate-spin' : ''}`} />
                    Refresh Live Rates
                  </button>
                </div>

                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="py-2.5 px-4">Currency</th>
                        <th className="py-2.5 px-4">Live Rate (1 Unit)</th>
                        <th className="py-2.5 px-4">Manual Override (₹)</th>
                        <th className="py-2.5 px-4">Effective Rate</th>
                        <th className="py-2.5 px-4 text-center">Status Indicator</th>
                        <th className="py-2.5 px-4 text-right">Example (₹890 Base)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-mono">
                      {SUPPORTED_CURRENCIES.map((c) => {
                        const live = liveRates[c.code] || (c.code === 'USD' ? 90.00 : c.code === 'EUR' ? 105.00 : 1.0);
                        const manualStr = manualRates[c.code] || '';
                        const res = resolveEffectiveExchangeRate(manualStr ? Number(manualStr) : null, live);
                        const exampleConverted = (890 / res.effectiveRate).toFixed(2);

                        return (
                          <tr key={c.code} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                            <td className="py-3 px-4 font-sans font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2">
                              <span className="text-base">{c.flag}</span>
                              <div>
                                <span className="font-bold">{c.code}</span>
                                <span className="text-xs text-slate-400 ml-1 font-normal font-sans">({c.name})</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-slate-600 dark:text-slate-400">
                              1 {c.code} = {formatINRPaise(live)}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-400 font-sans">₹</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  placeholder={live.toFixed(2)}
                                  value={manualStr}
                                  onChange={(e) => handleManualRateChange(c.code, e.target.value)}
                                  className="w-28 px-2 py-1 text-xs border border-slate-300 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono font-medium focus:outline-indigo-500"
                                />
                                {manualStr && (
                                  <button
                                    type="button"
                                    onClick={() => handleManualRateChange(c.code, '')}
                                    className="text-[10px] text-slate-400 hover:text-danger-text font-sans"
                                    title="Clear manual override to revert to live rate"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 font-bold text-slate-900 dark:text-slate-100">
                              1 {c.code} = {formatINRPaise(res.effectiveRate)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {res.isManualOverride ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warning/10 dark:bg-warning text-warning-text dark:text-warning-text border border-warning/25 dark:border-warning/40">
                                  Manual Override
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success/10 dark:bg-success text-success-text dark:text-success-text border border-success/25 dark:border-success/40">
                                  Live Rate
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-indigo-600 dark:text-indigo-400">
                              {c.symbol}{exampleConverted}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Standard Locale Preferences */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Base Master Currency</label>
                  <select
                    value={baseCurrency}
                    onChange={(e) => setBaseCurrency(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono"
                  >
                    <option value="INR">INR — Indian Rupee (₹) [Canonical Master]</option>
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">INR is canonical master for all product catalog prices.</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Tenant Timezone</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  >
                    <option value="Asia/Kolkata">Asia/Kolkata (IST +5:30)</option>
                    <option value="UTC">UTC (Coordinated Universal Time)</option>
                    <option value="America/New_York">America/New_York (EST/EDT)</option>
                    <option value="Europe/London">Europe/London (GMT/BST)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST +4:00)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Currency Decimal Precision</label>
                  <select
                    value={currencyPrecision}
                    onChange={(e) => setCurrencyPrecision(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  >
                    <option value={2}>2 Decimals (e.g. $9.89 / ₹890.00)</option>
                    <option value={3}>3 Decimals (e.g. 1.250)</option>
                    <option value={4}>4 Decimals (Precision Forex)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Quantity Decimal Precision</label>
                  <select
                    value={quantityPrecision}
                    onChange={(e) => setQuantityPrecision(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  >
                    <option value={0}>0 Decimals (Integers only)</option>
                    <option value={2}>2 Decimals (Standard units)</option>
                    <option value={3}>3 Decimals (e.g. 1.250 kg)</option>
                    <option value={4}>4 Decimals (Precision Process Units)</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-2"
                >
                  <Save className="w-4 h-4" /> Save Currency & Precision Settings
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default LocaleSettingsPage;
